<template>
	<div class="CountrySearch">
		<Multiselect
			v-bind="multiselectProps"
			@changeContinent="handleContinentChange"
			@updateValues="handleMultiselectUpdate"
		/>

		<span class="LocationHeader">Narrow by Country or Region</span>

		<div class="List">
			<CheckBoxSelectItem
				v-for="entry in topCountriesForSelectedContinent"
				:key="entry.item.value"
				:item="entry.item"
				:subItems="entry.subItems"
				@selectionChange="(state, changedItem) => handleCheckboxChange(changedItem || entry.item, state)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Multiselect from '@components/Multiselect/Multiselect.vue';
import CheckBoxSelectItem from '@components/CheckBoxSelectItem/CheckBoxSelectItem.vue';
import type  ISelectItem  from '@app/interfaces/dtos/ISelectItem';
import { SelectListItemStateType } from '@app/enums/SelectListItemStateType';
import { MultiselectMode } from '@app/enums/MultiselectMode';
import { useLocationFilterContext } from '@composables/useLocationFilterContext';

const {
	isMobile,
	showContinentOptions,
	selectedContinentName,
	topCountriesForSelectedContinent,
	multiselectOptionsForSelectedContinent,
	continentItems,
	selectedIds,
	handleSelect,
	handleContinentChange,
	footer
} = useLocationFilterContext();

const multiselectOptions = computed(() => {
	return showContinentOptions.value && isMobile.value
		? continentItems.value
		: multiselectOptionsForSelectedContinent.value;
});

const multiselectValue = ref<(string | number)[]>([...selectedIds.value]);

const multiselectProps = computed(() => ({
	options: multiselectOptions.value,
	modelValue: multiselectValue.value,
	mode: MultiselectMode.TAGS,
	placeholder: 'Search by Study Destination',
	selectedContinentName: selectedContinentName.value,
	footer: footer.value,
	sortSelectedToTop: true,
}));

watch(selectedIds, (val) => {
	multiselectValue.value = [...val];
}, { deep: true });

const handleCheckboxChange = (item: ISelectItem, state: string): void => {
	const selected = state === SelectListItemStateType.SELECTED;

	void handleSelect(item.value, selected);
};

const handleMultiselectUpdate = (value: (string | number)[] | string | number | null): void => {
	const normalized = Array.isArray(value) ? value : value ? [value] : [];
	const prevSet = new Set(multiselectValue.value);
	const nextSet = new Set(normalized);

	const added = normalized.filter((v) => !prevSet.has(v));
	const removed = multiselectValue.value.filter((v) => !nextSet.has(v));

	for (const add of added) void handleSelect(add, true);
	for (const rem of removed) void handleSelect(rem, false);

	multiselectValue.value = normalized;
};
</script>

<style scoped lang="scss">
.CountrySearch {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	@include AddBorderBeforeElement(1, 2); // required for outline not to be cut off

	.LocationHeader {
		color: $Grey-900;
		@include TextStyle(UiMediumBold);
	}

	.List {
		display: flex;
		flex-direction: column;
		overflow: auto;
		scrollbar-width: none;
		padding: 1rem 2px 0 0;	// required for outline not to be cut off
		border-top: 1px solid $Grey-200;

		&::-webkit-scrollbar {
			display: none;
		}
	}
}
</style>
